import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import User from "@mapmycustomers/shared/types/User";

import { ClusteredFeature } from "@app/component/map/FeaturedMap";
import userLocationStyleGetterFactory from "@app/scene/map/components/layers/UserLocation/util/userLocationStyleGetterFactory";
import { initUserLocations, selectUser } from "@app/scene/map/store/actions";
import BaseLayerData from "@app/scene/map/store/layers/BaseLayerData";
import {
  getAllHiddenUserIds,
  getSelectedUser,
  getUserLocationsData,
} from "@app/scene/map/store/selectors";
import isUserLocationLayerAvailable from "@app/scene/map/utils/isUserLocationLayerAvailable";
import { getMe, getOrganizationSettings } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import {
  getUserColorClassNameGetter,
  UserColorClassNameGetter,
} from "@app/store/userColor/selectors";
import defaultIdGetter from "@app/util/defaultIdGetter";

import Sidebar from "./component/SideBar";
import UserTooltip from "./component/UserTooltip";
import UserIdAvatarPair from "./type/UserIdAvatarPair";
import parseUserAvatarsBase64 from "./util/parseUserAvatarsBase64";
import { getUserLastLocationCoordinates } from "./util/userLocationGeometryGetter";

const isDifferent = (a: User, b: User) => a.id !== b.id;

interface Props {
  data: BaseLayerData;
  getUserColorClassName: UserColorClassNameGetter;
  hiddenOnMapUserIds: Set<User["id"]>;
  initUserLocations: typeof initUserLocations;
  isUserLayerAvailable: boolean;
  metadata: User["metaData"];
  onSelectUser: typeof selectUser;
  selectedUser?: User;
  users: User[];
}

const UserLocationLayer: React.FC<Props> = ({
  data,
  getUserColorClassName,
  hiddenOnMapUserIds,
  initUserLocations,
  isUserLayerAvailable,
  metadata,
  onSelectUser,
  selectedUser,
  users,
}) => {
  const [userIdToBase64Avatar, setUserIdToBase64Avatar] = useState<Map<User["id"], string>>(
    new Map()
  );

  const [hoveredUser, setHoveredUser] = useState<undefined | User>();

  useEffect(() => {
    parseUserAvatarsBase64(users, (pairs: UserIdAvatarPair[]) => {
      setUserIdToBase64Avatar(new Map(pairs.map(({ avatar, userId }) => [userId, avatar])));
    });
  }, [setUserIdToBase64Avatar, users]);

  useEffect(() => {
    initUserLocations({
      disabledUserIds: metadata.mapLegendUserLocationsDisabledUserIds ?? [],
      hiddenUserIds: metadata.mapLegendUserLocationsHiddenUserIds ?? [],
    });
    // we need to init this state only once, without changing of metadata
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initUserLocations]);

  const items = useMemo(
    () =>
      users.filter(
        ({ id, lastLocation }) =>
          !!lastLocation && !hiddenOnMapUserIds.has(id) && (!selectedUser || selectedUser.id === id)
      ),
    [hiddenOnMapUserIds, selectedUser, users]
  );

  const userClusterStyleGetter = useMemo(
    () =>
      userLocationStyleGetterFactory({
        getUserColorClassName,
        selectedUser,
        userIdToBase64Avatar,
      }),
    [getUserColorClassName, selectedUser, userIdToBase64Avatar]
  );

  const handleEntityMouseOver = useCallback((user: User) => setHoveredUser(user), [setHoveredUser]);

  const handleEntityMouseLeave = useCallback(() => setHoveredUser(undefined), [setHoveredUser]);

  return data.visible && isUserLayerAvailable ? (
    <>
      <ClusteredFeature<User>
        coordinateGetter={getUserLastLocationCoordinates}
        idGetter={defaultIdGetter}
        isDifferent={isDifferent}
        items={items}
        onClick={onSelectUser}
        onMouseLeave={handleEntityMouseLeave}
        onMouseOver={handleEntityMouseOver}
        styleGetter={userClusterStyleGetter}
        type="userLocationClusters"
      />
      {selectedUser && <Sidebar userIdToBase64Avatar={userIdToBase64Avatar} />}
      {hoveredUser && <UserTooltip user={hoveredUser} />}
    </>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  data: getUserLocationsData(state),
  getUserColorClassName: getUserColorClassNameGetter(state),
  hiddenOnMapUserIds: getAllHiddenUserIds(state),
  isUserLayerAvailable: isUserLocationLayerAvailable(getMe(state)!, getOrganizationSettings(state)),
  metadata: getMe(state)!.metaData,
  selectedUser: getSelectedUser(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  initUserLocations,
  onSelectUser: selectUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserLocationLayer);
